import type {
  ArtifactDiagnosticEvent,
  ArtifactAppendArgs,
  ArtifactBackend,
  ArtifactBeginArgs,
  ArtifactDiagnosticRefSummary,
  ArtifactDiagnosticSnapshotSummary,
  ArtifactKeyRef,
  ArtifactManagerConfig,
  ArtifactRemoveArgs,
  ArtifactRef,
  ArtifactSnapshot,
} from "./types";
import { ArtifactDiagnosticEventType } from "./types";

export class ArtifactManager<TChunk, TRef extends ArtifactRef = ArtifactRef> {
  private readonly backend: ArtifactBackend<TChunk, TRef>;
  private readonly diagnostics: ArtifactManagerConfig<TChunk, TRef>["diagnostics"];

  constructor({ backend, diagnostics }: ArtifactManagerConfig<TChunk, TRef>) {
    this.backend = backend;
    this.diagnostics = diagnostics;
  }

  async begin(args: ArtifactBeginArgs): Promise<void> {
    this.emitDiagnosticEvent({
      type: ArtifactDiagnosticEventType.BeginStarted,
      key: args.key,
      mimeType: args.mimeType,
    });

    try {
      await this.backend.begin(args);
      this.emitDiagnosticEvent({
        type: ArtifactDiagnosticEventType.BeginSucceeded,
        key: args.key,
        mimeType: args.mimeType,
      });
    } catch (error) {
      this.emitDiagnosticEvent({
        type: ArtifactDiagnosticEventType.BeginFailed,
        key: args.key,
        mimeType: args.mimeType,
        errorMessage: this.toErrorMessage({ error }),
      });
      throw error;
    }
  }

  async append(args: ArtifactAppendArgs<TChunk>): Promise<void> {
    try {
      await this.backend.append(args);
    } catch (error) {
      this.emitDiagnosticEvent({
        type: ArtifactDiagnosticEventType.AppendFailed,
        key: args.key,
        errorMessage: this.toErrorMessage({ error }),
      });
      throw error;
    }
  }

  async finalize(args: ArtifactKeyRef): Promise<TRef> {
    this.emitDiagnosticEvent({
      type: ArtifactDiagnosticEventType.FinalizeStarted,
      key: args.key,
    });

    try {
      const finalizedArtifact = await this.backend.finalize(args);
      this.emitDiagnosticEvent({
        type: ArtifactDiagnosticEventType.FinalizeSucceeded,
        key: args.key,
        artifact: this.buildRefSummary({ artifact: finalizedArtifact }),
      });
      return finalizedArtifact;
    } catch (error) {
      this.emitDiagnosticEvent({
        type: ArtifactDiagnosticEventType.FinalizeFailed,
        key: args.key,
        errorMessage: this.toErrorMessage({ error }),
      });
      throw error;
    }
  }

  async getSnapshot(args: ArtifactKeyRef): Promise<ArtifactSnapshot<TRef> | null> {
    try {
      const snapshot = await this.backend.getSnapshot(args);
      this.emitDiagnosticEvent({
        type: ArtifactDiagnosticEventType.GetSnapshotResolved,
        key: args.key,
        hasSnapshot: snapshot !== null,
        snapshot: snapshot ? this.buildSnapshotSummary({ snapshot }) : undefined,
      });
      return snapshot;
    } catch (error) {
      this.emitDiagnosticEvent({
        type: ArtifactDiagnosticEventType.GetSnapshotFailed,
        key: args.key,
        errorMessage: this.toErrorMessage({ error }),
      });
      throw error;
    }
  }

  async getRef(args: ArtifactKeyRef): Promise<TRef | null> {
    try {
      const artifactRef = await this.backend.getRef(args);
      this.emitDiagnosticEvent({
        type: ArtifactDiagnosticEventType.GetRefResolved,
        key: args.key,
        hasRef: artifactRef !== null,
        ref: artifactRef ? this.buildRefSummary({ artifact: artifactRef }) : undefined,
      });
      return artifactRef;
    } catch (error) {
      this.emitDiagnosticEvent({
        type: ArtifactDiagnosticEventType.GetRefFailed,
        key: args.key,
        errorMessage: this.toErrorMessage({ error }),
      });
      throw error;
    }
  }

  async remove(args: ArtifactRemoveArgs): Promise<void> {
    this.emitDiagnosticEvent({
      type: ArtifactDiagnosticEventType.RemoveStarted,
      key: args.key,
      removeReason: args.removeReason,
    });

    try {
      await this.backend.remove(args);
      this.emitDiagnosticEvent({
        type: ArtifactDiagnosticEventType.RemoveSucceeded,
        key: args.key,
        removeReason: args.removeReason,
      });
    } catch (error) {
      this.emitDiagnosticEvent({
        type: ArtifactDiagnosticEventType.RemoveFailed,
        key: args.key,
        removeReason: args.removeReason,
        errorMessage: this.toErrorMessage({ error }),
      });
      throw error;
    }
  }

  private readonly buildRefSummary = ({
    artifact,
  }: {
    artifact: ArtifactRef;
  }): ArtifactDiagnosticRefSummary => ({
    artifactId: artifact.artifactId,
    key: artifact.key,
    mimeType: artifact.mimeType,
    sizeBytes: artifact.sizeBytes,
  });

  private readonly buildSnapshotSummary = ({
    snapshot,
  }: {
    snapshot: ArtifactSnapshot<TRef>;
  }): ArtifactDiagnosticSnapshotSummary => ({
    ...this.buildRefSummary({ artifact: snapshot }),
    state: snapshot.state,
  });

  private readonly toErrorMessage = ({ error }: { error: unknown }): string => {
    if (error instanceof Error) {
      return error.message;
    }

    return String(error);
  };

  private readonly emitDiagnosticEvent = (event: ArtifactDiagnosticEvent): void => {
    try {
      this.diagnostics?.onEvent?.({ event });
    } catch {
      // Diagnostics are best-effort and must not change artifact behavior.
    }
  };
}
